import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TextInput, StyleSheet, TouchableOpacity,
  Alert, ScrollView, ActivityIndicator, Modal, Switch,
  Image, useWindowDimensions, Pressable, Animated, Easing,
} from 'react-native';
import {
  getRestaurantsByBranch, registerRestaurant, updateRestaurant,
  deleteRestaurant, uploadRestaurantImage, deleteStorageUrl,
} from '../query/restaurantService';
import { fetchBranches, BranchModel } from '../query/branchService';

interface RestaurantManagerProps { branchId?: string; }

const C = {
  primary: '#29A05A', primaryLight: 'rgba(41,160,90,0.15)', success: '#10b981', successLight: 'rgba(16,185,129,0.15)',
  danger: '#ef4444', dangerLight: 'rgba(239,68,68,0.15)', warning: '#f59e0b',
  bg: '#0E1512', textPrimary: '#E2EDE6', textMuted: '#7A9A80', border: 'rgba(255,255,255,0.09)', rowAlt: '#1A2820',
};

const PAGE_SIZE = 10;
const TABLE_MIN_WIDTH = 1260;
const COLS = {
  sn: 0.4,
  store: 2.4,
  phone: 1.3,
  charges: 1.6,
  rating: 1.0,
  actions: 3.7,
};

const TOGGLES: { f: string; label: string; color: string }[] = [
  { f: 'active', label: 'Active', color: '#10b981' },
  { f: 'online', label: 'Online', color: '#1d4ed8' },
  { f: 'premium', label: 'Premium', color: '#f59e0b' },
  { f: 'show', label: 'Show', color: '#21808D' },
];

const STEPS = [
  { title: 'Account Setup',    sub: 'Login credentials for this restaurant', icon: '🔑' },
  { title: 'Basic Info',       sub: 'Store name and category',               icon: '🍽️' },
  { title: 'Address & Branch', sub: 'Location and branch assignment',        icon: '🏠' },
  { title: 'Charges',          sub: 'Delivery fees and minimums',            icon: '💰' },
  { title: 'Map Location',     sub: 'Coordinates and map details',           icon: '📍' },
  { title: 'Content',          sub: 'Features, announcements and policy',    icon: '📝' },
  { title: 'Status',           sub: 'Availability and visibility settings',  icon: '⚙️' },
  { title: 'Media',            sub: 'Profile image and banners',             icon: '🖼️' },
];

const emptyForm = () => ({
  storename: '', category: 'restaurant', phone: '', address: '',
  sublocality: '', pincode: '', mapaddress: '', mappin: '',
  delivery: 15, kmcharge: 5, minAmount: 149, packing: 10,
  service: 0, tax: 0, range: 20, rating: 5,
  storeLat: '', storeLon: '', branchId: '', storeuid: '', uid: '', id: '',
  feature: 'Deliver by Zapee', announcement: '', policy: '',
  image: '', bannerImages: [] as string[],
  active: true, online: false, premium: true, show: true,
  email: '',
});

// ── Form field — defined outside component to prevent focus loss on re-render ──
const F = ({ label, value, onChange, keyType, multiline, readonly }: any) => (
  <View style={wi.fg}>
    <Text style={wi.label}>{label}</Text>
    <TextInput
      style={[wi.input, multiline && { height: 72 }, readonly && { backgroundColor: '#141E18', color: C.textMuted }]}
      placeholder={label} value={String(value ?? '')} onChangeText={readonly ? undefined : onChange}
      keyboardType={keyType || 'default'} multiline={multiline}
      placeholderTextColor={C.textMuted} editable={!readonly} autoCapitalize="none"
    />
  </View>
);

const SwitchRow = ({ label, sub, val, onVal }: any) => (
  <View style={wi.switchRow}>
    <View>
      <Text style={wi.switchLabel}>{label}</Text>
      {sub ? <Text style={wi.switchSub}>{sub}</Text> : null}
    </View>
    <Switch value={val} onValueChange={onVal} trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
  </View>
);

const TBIcon = ({ name }: { name: string }) => (
  <span className="material-icons-round" style={{ fontSize: 18, color: '#4ECDC4', lineHeight: 1 }}>{name}</span>
);

export default function RestaurantManager({ branchId = '' }: RestaurantManagerProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 600;

  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [branches, setBranches] = useState<BranchModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState<any>(emptyForm());
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [step, setStep] = useState(0);
  const [page, setPage] = useState(1);

  // Delete confirm + toast (web-safe; Alert.alert buttons don't render on web)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const toastTimer = useRef<any>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  // Right-drawer slide animation for the create/edit wizard
  const drawerW = Math.min(isMobile ? width : 560, width);
  const slideX = useRef(new Animated.Value(drawerW)).current;
  useEffect(() => {
    if (modalVisible) {
      slideX.setValue(drawerW);
      Animated.timing(slideX, { toValue: 0, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [modalVisible, drawerW, slideX]);
  const closeDrawer = () => {
    Animated.timing(slideX, { toValue: drawerW, duration: 220, easing: Easing.in(Easing.cubic), useNativeDriver: true })
      .start(() => setModalVisible(false));
  };

  // Auth creds (create only)
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // Image upload
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerFiles, setBannerFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [imgSaving, setImgSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getRestaurantsByBranch(branchId);
      setRestaurants(data); setFiltered(data);
    } catch { Alert.alert('Error', 'Failed to load restaurants'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [branchId]);
  useEffect(() => { fetchBranches().then(setBranches).catch(console.error); }, []);
  useEffect(() => {
    if (!searchQuery.trim()) { setFiltered(restaurants); return; }
    const q = searchQuery.toLowerCase();
    setFiltered(restaurants.filter(r =>
      (r.storename || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q)
    ));
  }, [searchQuery, restaurants]);

  useEffect(() => { setPage(1); }, [searchQuery, restaurants.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const set = (field: string, val: any) => setFormData((p: any) => ({ ...p, [field]: val }));

  const openCreate = () => {
    setFormData({ ...emptyForm(), branchId });
    setEditingDocId(null); setStep(0);
    setRegEmail(''); setRegPassword('');
    setImageFile(null); setImagePreview(null); setBannerFiles([]);
    setModalVisible(true);
  };

  const openEdit = (item: any) => {
    setFormData({ ...emptyForm(), ...item });
    setEditingDocId(item.docId); setStep(0);
    setRegEmail(''); setRegPassword('');
    setImageFile(null); setImagePreview(item.image || null); setBannerFiles([]);
    setModalVisible(true);
  };

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!editingDocId && !regEmail.trim()) { Alert.alert('Required', 'Email is required'); return false; }
      if (!editingDocId && regPassword.length < 6) { Alert.alert('Required', 'Password must be at least 6 characters'); return false; }
    }
    if (step === 1) {
      if (!formData.storename?.trim()) { Alert.alert('Required', 'Store name is required'); return false; }
    }
    return true;
  };

  const goNext = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const onSubmit = async () => {
    if (!validateStep()) return;
    setImgSaving(true);
    try {
      const docId = editingDocId || `temp_${Date.now()}`;
      let imageUrl = formData.image || '';
      if (imageFile) imageUrl = await uploadRestaurantImage(imageFile, docId, 'main');

      const existingBanners: string[] = Array.isArray(formData.bannerImages) ? formData.bannerImages : [];
      const newBannerUrls = await Promise.all(
        bannerFiles.map((f, i) => uploadRestaurantImage(f, docId, `banner${i}_${Date.now()}`))
      );
      const bannerImages = [...existingBanners, ...newBannerUrls];
      const payload = { ...formData, image: imageUrl, bannerImages };

      if (editingDocId) {
        await updateRestaurant(editingDocId, payload);
      } else {
        await registerRestaurant(regEmail.trim(), regPassword, payload);
      }
      closeDrawer(); load();
      showToast(`Restaurant ${editingDocId ? 'updated' : 'registered'}`);
    } catch (e: any) { showToast(e.message || 'Failed to save', 'error'); }
    setImgSaving(false);
  };

  // Quick-toggle a boolean field straight from the row.
  // Sends the FULL item (one field flipped) — toNestedRestaurant rebuilds the
  // whole payload, so a partial write would clobber other fields.
  const toggleField = async (item: any, field: string) => {
    const id = item.docId;
    if (!id) return;
    const key = item.docId || item.id;
    const newVal = !item[field];
    setRestaurants(prev => prev.map(r => (r.docId || r.id) === key ? { ...r, [field]: newVal } : r));
    try {
      await updateRestaurant(id, { ...item, [field]: newVal });
    } catch {
      setRestaurants(prev => prev.map(r => (r.docId || r.id) === key ? { ...r, [field]: !newVal } : r));
      showToast(`Failed to update ${field}`, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRestaurant(deleteTarget.docId, deleteTarget.image, deleteTarget.bannerImages);
      setRestaurants(prev => prev.filter(r => (r.docId || r.id) !== (deleteTarget.docId || deleteTarget.id)));
      showToast('Restaurant deleted');
      setDeleteTarget(null);
    } catch {
      showToast('Failed to delete restaurant', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const removeBanner = async (url: string) => {
    await deleteStorageUrl(url);
    set('bannerImages', formData.bannerImages.filter((u: string) => u !== url));
  };


  const selectStyle: React.CSSProperties = {
    width: '100%', height: 46, backgroundColor: '#fafafa',
    border: '1px solid #e2e8f0', borderRadius: 10,
    paddingLeft: 14, fontSize: 14, color: '#E2EDE6', cursor: 'pointer', outline: 'none',
  };

  // ── Step content ──────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0:
        return editingDocId ? (
          <>
            <F label="Email (read-only)" value={formData.email || regEmail} readonly />
            <View style={wi.infoBox}>
              <Text style={wi.infoTxt}>To change login credentials, re-register the restaurant.</Text>
            </View>
          </>
        ) : (
          <>
            <F label="Email *" value={regEmail} onChange={setRegEmail} keyType="email-address" />
            <F label="Password * (min 6 chars)" value={regPassword} onChange={setRegPassword} />
          </>
        );
      case 1:
        return (
          <>
            <F label="Store Name *" value={formData.storename} onChange={(t: string) => set('storename', t)} />
            <F label="Category" value={formData.category} onChange={(t: string) => set('category', t)} />
            <F label="Phone" value={formData.phone} onChange={(t: string) => set('phone', t)} keyType="phone-pad" />
          </>
        );
      case 2:
        return (
          <>
            <F label="Address" value={formData.address} onChange={(t: string) => set('address', t)} multiline />
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Sublocality" value={formData.sublocality} onChange={(t: string) => set('sublocality', t)} /></View>
              <View style={{ flex: 1 }}><F label="Pincode" value={formData.pincode} onChange={(t: string) => set('pincode', t)} /></View>
            </View>
            <View style={wi.fg}>
              <Text style={wi.label}>Branch *</Text>
              <select value={formData.branchId} onChange={(e: any) => set('branchId', e.target.value)} style={selectStyle}>
                <option value="">— Select Branch —</option>
                {branches.map(b => <option key={b.uid} value={b.branchId}>{b.storename || b.branchId}</option>)}
              </select>
            </View>
          </>
        );
      case 3:
        return (
          <>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Delivery (₹)" value={String(formData.delivery)} onChange={(t: string) => set('delivery', Number(t) || 0)} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="KM Charge" value={String(formData.kmcharge)} onChange={(t: string) => set('kmcharge', Number(t) || 0)} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Min Amount (₹)" value={String(formData.minAmount)} onChange={(t: string) => set('minAmount', Number(t) || 0)} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Packing (₹)" value={String(formData.packing)} onChange={(t: string) => set('packing', Number(t) || 0)} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Service (₹)" value={String(formData.service)} onChange={(t: string) => set('service', Number(t) || 0)} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Tax (%)" value={String(formData.tax)} onChange={(t: string) => set('tax', Number(t) || 0)} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Range (km)" value={String(formData.range)} onChange={(t: string) => set('range', Number(t) || 0)} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Rating" value={String(formData.rating)} onChange={(t: string) => set('rating', Number(t) || 0)} keyType="numeric" /></View>
            </View>
          </>
        );
      case 4:
        return (
          <>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Latitude" value={formData.storeLat} onChange={(t: string) => set('storeLat', t)} /></View>
              <View style={{ flex: 1 }}><F label="Longitude" value={formData.storeLon} onChange={(t: string) => set('storeLon', t)} /></View>
            </View>
            <F label="Map Address" value={formData.mapaddress} onChange={(t: string) => set('mapaddress', t)} />
            <F label="Map Pin" value={formData.mappin} onChange={(t: string) => set('mappin', t)} />
          </>
        );
      case 5:
        return (
          <>
            <F label="Feature Tag" value={formData.feature} onChange={(t: string) => set('feature', t)} />
            <F label="Announcement" value={formData.announcement} onChange={(t: string) => set('announcement', t)} multiline />
            <F label="Policy" value={formData.policy} onChange={(t: string) => set('policy', t)} multiline />
          </>
        );
      case 6:
        return (
          <>
            <SwitchRow label="Active" sub="Restaurant is active" val={!!formData.active} onVal={(v: boolean) => set('active', v)} />
            <SwitchRow label="Online" sub="Currently accepting orders" val={!!formData.online} onVal={(v: boolean) => set('online', v)} />
            <SwitchRow label="Premium" sub="Featured restaurant" val={!!formData.premium} onVal={(v: boolean) => set('premium', v)} />
            <SwitchRow label="Show" sub="Visible to customers" val={!!formData.show} onVal={(v: boolean) => set('show', v)} />
          </>
        );
      case 7:
        return (
          <>
            {/* Main image */}
            <Text style={wi.sectionLabel}>Profile Image</Text>
            <View style={wi.imgRow}>
              <View style={wi.imgPicker}>
                {imagePreview
                  ? <Image source={{ uri: imagePreview }} style={wi.imgPreview} />
                  : <View style={wi.imgPlaceholder}><Text style={{ fontSize: 28 }}>🍽️</Text><Text style={wi.imgPlaceholderTxt}>Tap to upload</Text></View>
                }
                <input type="file" accept="image/*"
                  onChange={(e: any) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setImageFile(f); setImagePreview(URL.createObjectURL(f));
                  }}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
              </View>
              {imagePreview && (
                <TouchableOpacity style={wi.removeBtn} onPress={async () => {
                  if (formData.image && imageFile === null) await deleteStorageUrl(formData.image);
                  setImageFile(null); setImagePreview(null); set('image', '');
                }}>
                  <Text style={wi.removeBtnTxt}>✕ Remove</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Banners */}
            <Text style={[wi.sectionLabel, { marginTop: 16 }]}>Banner Images</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
              {(formData.bannerImages || []).map((url: string, i: number) => (
                <View key={i} style={wi.bannerThumb}>
                  <Image source={{ uri: url }} style={wi.bannerImg} />
                  <TouchableOpacity style={wi.bannerDel} onPress={() => removeBanner(url)}>
                    <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
              <TouchableOpacity style={wi.bannerAdd}>
                <Text style={{ fontSize: 22 }}>＋</Text>
                <Text style={wi.imgPlaceholderTxt}>Add Banner</Text>
                <input type="file" accept="image/*" multiple
                  onChange={(e: any) => {
                    const files = Array.from(e.target.files as FileList) as File[];
                    setBannerFiles(prev => [...prev, ...files]);
                  }}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
              </TouchableOpacity>
              {bannerFiles.map((f, i) => (
                <View key={`new_${i}`} style={[wi.bannerThumb, { borderColor: C.success }]}>
                  <Image source={{ uri: URL.createObjectURL(f) }} style={wi.bannerImg} />
                  <TouchableOpacity style={[wi.bannerDel, { backgroundColor: C.success }]} onPress={() => setBannerFiles(prev => prev.filter((_, j) => j !== i))}>
                    <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          </>
        );
      default: return null;
    }
  };

  // ── Table row ─────────────────────────────────────────────────────
  const Row = ({ item, index }: { item: any; index: number }) => (
    <View style={[tb.row, index % 2 === 1 && { backgroundColor: C.rowAlt }]}>
      {/* # */}
      <View style={[tb.td, { flex: COLS.sn, alignItems: 'center' }]}>
        <Text style={tb.snTxt}>{(safePage - 1) * PAGE_SIZE + index + 1}</Text>
      </View>
      {/* Store */}
      <View style={[tb.td, { flex: COLS.store, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        {item.image
          ? <Image source={{ uri: item.image }} style={tb.img} />
          : <View style={tb.imgPlaceholder}><Text style={{ fontSize: 18 }}>🍽️</Text></View>}
        <View style={{ flex: 1 }}>
          <Text style={tb.name} numberOfLines={1}>{item.storename || 'Untitled'}</Text>
          <Text style={tb.sub} numberOfLines={1}>{item.category || '—'}{item.email ? ` · ${item.email}` : ''}</Text>
        </View>
      </View>
      {/* Phone */}
      <View style={[tb.td, { flex: COLS.phone }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>{item.phone || '—'}</Text>
      </View>
      {/* Charges */}
      <View style={[tb.td, { flex: COLS.charges }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>₹{item.delivery} delivery</Text>
        <Text style={tb.cellSub} numberOfLines={1}>₹{item.packing} pack · min ₹{item.minAmount}</Text>
      </View>
      {/* Rating / range */}
      <View style={[tb.td, { flex: COLS.rating }]}>
        <Text style={tb.cellTxt}>⭐ {item.rating || 0}</Text>
        <Text style={tb.cellSub}>{item.range} km</Text>
      </View>
      {/* Actions: boolean toggles + edit/delete */}
      <View style={[tb.td, { flex: COLS.actions, flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }]}>
        {TOGGLES.map(t => {
          const on = !!item[t.f];
          return (
            <TouchableOpacity
              key={t.f}
              style={[tb.tgl, { backgroundColor: on ? t.color : '#141E18', borderColor: on ? t.color : C.border }]}
              onPress={() => toggleField(item, t.f)}
            >
              <Text style={[tb.tglTxt, { color: on ? '#fff' : C.textMuted }]}>{t.label}</Text>
            </TouchableOpacity>
          );
        })}
        <TouchableOpacity style={tb.editBtn} onPress={() => openEdit(item)}><Text style={tb.editTxt}>Edit</Text></TouchableOpacity>
        <TouchableOpacity style={tb.delBtn} onPress={() => setDeleteTarget(item)}><Text style={tb.delTxt}>Delete</Text></TouchableOpacity>
      </View>
    </View>
  );

  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  return (
    <View style={cs.container}>
      <View style={[cs.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
        <View style={cs.tbTitle}>
          <TBIcon name="restaurant" />
          <View>
            <Text style={cs.tbTitleText}>Restaurants</Text>
            <Text style={cs.tbTitleSub}>Restaurant partners & menus</Text>
          </View>
        </View>
        <View style={cs.searchBar}>
          <Text style={{ fontSize: 14, marginRight: 8 }}>🔍</Text>
          <TextInput style={cs.searchInput} placeholder="Search restaurants..." value={searchQuery} onChangeText={setSearchQuery} placeholderTextColor="#8BAAB8" />
          {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><Text style={{ color: '#8BAAB8', padding: 4 }}>✕</Text></TouchableOpacity> : null}
        </View>
        <TouchableOpacity style={cs.addBtn} onPress={openCreate}>
          <Text style={cs.addBtnTxt}>+ Register Restaurant</Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <View style={cs.loading}><ActivityIndicator size="large" color={C.primary} /></View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: TABLE_MIN_WIDTH, flexGrow: 1 }}>
          <View style={tb.table}>
            <View style={tb.headerRow}>
              <Th label="#" flex={COLS.sn} align="center" />
              <Th label="Restaurant" flex={COLS.store} />
              <Th label="Phone" flex={COLS.phone} />
              <Th label="Charges" flex={COLS.charges} />
              <Th label="Rating" flex={COLS.rating} />
              <Th label="Actions" flex={COLS.actions} align="right" />
            </View>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
              {filtered.length === 0 ? (
                <View style={cs.empty}><Text style={{ fontSize: 48 }}>🍽️</Text><Text style={cs.emptyTitle}>No restaurants found</Text></View>
              ) : (
                paged.map((item, index) => <Row key={item.docId || item.id} item={item} index={index} />)
              )}
            </ScrollView>
          </View>
        </ScrollView>
      )}

      {!loading && filtered.length > 0 && (
        <View style={[cs.bottomBar, isMobile && { flexDirection: 'column', gap: 10 }]}>
          <Text style={cs.footerCount}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} restaurants
          </Text>
          <View style={cs.pageBtns}>
            <TouchableOpacity style={[cs.pageBtn, safePage <= 1 && cs.pageBtnDisabled]} onPress={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}>
              <Text style={[cs.pageBtnTxt, safePage <= 1 && cs.pageBtnTxtDisabled]}>‹ Prev</Text>
            </TouchableOpacity>
            <Text style={cs.pageInfo}>Page {safePage} of {totalPages}</Text>
            <TouchableOpacity style={[cs.pageBtn, safePage >= totalPages && cs.pageBtnDisabled]} onPress={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
              <Text style={[cs.pageBtnTxt, safePage >= totalPages && cs.pageBtnTxtDisabled]}>Next ›</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* ── Step Wizard (right drawer) ── */}
      <Modal visible={modalVisible} animationType="fade" transparent onRequestClose={closeDrawer}>
        <View style={wi.root}>
          <Pressable style={wi.backdrop} onPress={closeDrawer} />
          <Animated.View style={[wi.drawer, { width: drawerW, transform: [{ translateX: slideX }] }]}>
        <View style={wi.screen}>
          <View style={wi.topBar}>
            <TouchableOpacity style={wi.closeBtn} onPress={closeDrawer}>
              <Text style={wi.closeTxt}>✕</Text>
            </TouchableOpacity>
            <Text style={wi.topTitle}>{editingDocId ? 'Edit Restaurant' : 'Register Restaurant'}</Text>
            <View style={{ width: 36 }} />
          </View>

          <View style={wi.progressArea}>
            <View style={wi.progressBarOuter}>
              <View style={[wi.progressBarInner, { width: `${((step + 1) / STEPS.length) * 100}%` as any }]} />
            </View>
            <View style={wi.progressRow}>
              <Text style={wi.progressLabel}>{STEPS[step].icon}  {STEPS[step].title}</Text>
              <Text style={wi.progressCount}>{step + 1} / {STEPS.length}</Text>
            </View>
            <Text style={wi.progressSub}>{STEPS[step].sub}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={wi.dots}>
                {STEPS.map((_, i) => (
                  <TouchableOpacity key={i} onPress={() => setStep(i)} style={[wi.dot, i === step && wi.dotActive, i < step && wi.dotDone]}>
                    {i < step
                      ? <Text style={wi.dotTxtDone}>✓</Text>
                      : <Text style={[wi.dotTxt, i === step && { color: '#fff' }]}>{i + 1}</Text>
                    }
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={wi.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {renderStep()}
          </ScrollView>

          <View style={wi.footer}>
            {step > 0
              ? <TouchableOpacity style={wi.backBtn} onPress={goBack}><Text style={wi.backBtnTxt}>← Back</Text></TouchableOpacity>
              : <View style={{ flex: 1 }} />
            }
            {step < STEPS.length - 1 ? (
              <TouchableOpacity style={wi.nextBtn} onPress={goNext}>
                <Text style={wi.nextBtnTxt}>Next →</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={[wi.saveBtn, (imgSaving || uploading) && { opacity: 0.6 }]} onPress={onSubmit} disabled={imgSaving || uploading}>
                {(imgSaving || uploading) ? <ActivityIndicator color="#fff" size="small" /> :
                  <Text style={wi.saveBtnTxt}>{editingDocId ? 'Update Restaurant' : 'Register Restaurant'}</Text>}
              </TouchableOpacity>
            )}
          </View>
        </View>
          </Animated.View>
        </View>
      </Modal>

      {/* ── Delete confirm modal (web-safe) ── */}
      <Modal visible={!!deleteTarget} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !deleting && setDeleteTarget(null)}>
          <Pressable style={cf.card} onPress={() => {}}>
            <View style={cf.iconWrap}><Text style={{ fontSize: 26 }}>🗑️</Text></View>
            <Text style={cf.title}>Delete restaurant?</Text>
            <Text style={cf.body}>
              {deleteTarget ? `This permanently deletes "${deleteTarget.storename || 'this restaurant'}" and its images. This cannot be undone.` : ''}
            </Text>
            <View style={cf.actions}>
              <TouchableOpacity style={cf.cancelBtn} onPress={() => setDeleteTarget(null)} disabled={deleting}>
                <Text style={cf.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[cf.confirmBtn, deleting && { opacity: 0.6 }]} onPress={confirmDelete} disabled={deleting}>
                {deleting ? <ActivityIndicator color="#fff" size="small" /> : <Text style={cf.confirmTxt}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <View style={[cs.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.success }]}>
          <Text style={cs.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

// ── Card styles ───────────────────────────────────────────────────
const cs = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0E1512', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  headerM: { flexDirection: 'column', alignItems: 'flex-start', gap: 12 },
  pageTitle: { fontSize: 24, fontWeight: '700', color: '#E2EDE6' },
  pageSub: { fontSize: 13, color: '#7A9A80', marginTop: 2 },
  addBtn: { backgroundColor: '#4ECDC4', paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8 },
  addBtnTxt: { color: '#0A1712', fontWeight: '700', fontSize: 12 },
  controls: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)', flexWrap: 'wrap' },
  tbTitle: { flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any },
  tbTitleText: { fontSize: 15, fontWeight: '800', color: '#fff' },
  tbTitleSub: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  searchBar: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(33,128,141,0.25)', paddingHorizontal: 10, height: 36 },
  searchInput: { flex: 1, fontSize: 13, color: '#e2f0f2', outlineStyle: 'none' } as any,
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  card: { flex: 1, backgroundColor: '#17201B', borderRadius: 16, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 3, borderTopWidth: 3, borderTopColor: '#21808D' },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 12 },
  cardImg: { width: 48, height: 48, borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  cardImgPlaceholder: { width: 48, height: 48, borderRadius: 12, backgroundColor: '#0E1512', justifyContent: 'center', alignItems: 'center' },
  name: { fontSize: 15, fontWeight: '700', color: '#E2EDE6' },
  category: { fontSize: 12, color: '#7A9A80', marginTop: 2 },
  email: { fontSize: 11, color: '#21808D', marginTop: 1 },
  badgeCol: { alignItems: 'flex-end' },
  badge: { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  badgeTxt: { fontSize: 11, fontWeight: '600' },
  infoGrid: { gap: 6, marginBottom: 12 },
  infoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  infoIcon: { fontSize: 13, width: 20 },
  infoTxt: { fontSize: 13, color: '#7A9A80', flex: 1 },
  actions: { flexDirection: 'row', gap: 10 },
  editBtn: { flex: 1, backgroundColor: 'rgba(41,160,90,0.12)', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  editTxt: { color: '#21808D', fontWeight: '600', fontSize: 13 },
  delBtn: { flex: 1, backgroundColor: 'rgba(239,68,68,0.12)', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  delTxt: { color: '#ef4444', fontWeight: '600', fontSize: 13 },
  empty: { alignItems: 'center', paddingTop: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#E2EDE6', marginTop: 12 },

  bottomBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingVertical: 10, paddingHorizontal: 14, marginTop: 8 },
  footerCount: { fontSize: 12, color: C.textMuted, fontWeight: '600' },
  pageBtns: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  pageBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, borderWidth: 1, borderColor: C.border, backgroundColor: '#17201B' },
  pageBtnDisabled: { backgroundColor: '#141E18', borderColor: 'rgba(255,255,255,0.06)' },
  pageBtnTxt: { fontSize: 13, fontWeight: '700', color: C.primary },
  pageBtnTxtDisabled: { color: '#cbd5e1' },
  pageInfo: { fontSize: 13, fontWeight: '600', color: C.textPrimary, minWidth: 96, textAlign: 'center' },

  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

// ── Table styles ──────────────────────────────────────────────────
const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#17201B', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#0A1712', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' as const },

  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#141E18' },
  td: { justifyContent: 'center', paddingRight: 8 },
  snTxt: { fontSize: 12, fontWeight: '700', color: C.textMuted },
  cellTxt: { fontSize: 13, color: C.textPrimary },
  cellSub: { fontSize: 11, color: C.textMuted, marginTop: 1 },

  img: { width: 40, height: 40, borderRadius: 10, borderWidth: 1, borderColor: C.border },
  imgPlaceholder: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#0E1512', justifyContent: 'center', alignItems: 'center' },
  name: { fontSize: 14, fontWeight: '600', color: C.textPrimary },
  sub: { fontSize: 11, color: C.textMuted, marginTop: 1 },

  badge: { borderRadius: 7, paddingHorizontal: 8, paddingVertical: 3 },
  badgeTxt: { fontSize: 11, fontWeight: '700' },

  tgl: { borderRadius: 14, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 5 },
  tglTxt: { fontSize: 11, fontWeight: '700' },

  editBtn: { backgroundColor: C.primaryLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  editTxt: { color: C.primary, fontWeight: '700', fontSize: 12 },
  delBtn: { backgroundColor: C.dangerLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  delTxt: { color: C.danger, fontWeight: '700', fontSize: 12 },
});

// ── Delete confirm modal styles ───────────────────────────────────
const cf = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#17201B', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 10 },
  iconWrap: { width: 56, height: 56, borderRadius: 28, backgroundColor: C.dangerLight, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 18, fontWeight: '700', color: C.textPrimary, marginBottom: 6 },
  body: { fontSize: 13, color: C.textMuted, textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  actions: { flexDirection: 'row', gap: 10, width: '100%' },
  cancelBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.textPrimary, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.danger, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});

// ── Wizard styles ─────────────────────────────────────────────────
const wi = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#17201B', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
  screen: { flex: 1, backgroundColor: '#17201B' },
  topBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, backgroundColor: '#17201B', borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12 },
  closeBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#141E18', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: '#7A9A80', fontWeight: '600' },
  topTitle: { flex: 1, fontSize: 18, fontWeight: '700', color: '#E2EDE6', textAlign: 'center' },

  progressArea: { backgroundColor: '#141E18', paddingHorizontal: 20, paddingTop: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  progressBarOuter: { height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, marginBottom: 10 },
  progressBarInner: { height: 4, backgroundColor: '#21808D', borderRadius: 2 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  progressLabel: { fontSize: 15, fontWeight: '700', color: '#E2EDE6' },
  progressCount: { fontSize: 12, color: '#7A9A80', fontWeight: '600' },
  progressSub: { fontSize: 12, color: '#7A9A80', marginBottom: 10 },
  dots: { flexDirection: 'row', gap: 6 },
  dot: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#e2e8f0', justifyContent: 'center', alignItems: 'center' },
  dotActive: { backgroundColor: '#21808D' },
  dotDone: { backgroundColor: '#10b981' },
  dotTxt: { fontSize: 11, fontWeight: '700', color: '#7A9A80' },
  dotTxtDone: { fontSize: 11, fontWeight: '700', color: '#fff' },

  scrollContent: { padding: 20, paddingBottom: 24 },

  footer: { flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#e2e8f0', backgroundColor: '#17201B' },
  backBtn: { flex: 1, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  backBtnTxt: { color: '#7A9A80', fontWeight: '600', fontSize: 15 },
  nextBtn: { flex: 2, backgroundColor: '#21808D', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  nextBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
  saveBtn: { flex: 2, backgroundColor: '#10b981', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  saveBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },

  fg: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#E2EDE6', backgroundColor: '#fafafa' },
  row: { flexDirection: 'row', gap: 12 },

  infoBox: { backgroundColor: 'rgba(41,160,90,0.12)', borderRadius: 10, padding: 12, marginBottom: 14 },
  infoTxt: { fontSize: 13, color: '#21808D', fontWeight: '500' },

  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#141E18' },
  switchLabel: { fontSize: 14, color: '#E2EDE6', fontWeight: '600' },
  switchSub: { fontSize: 12, color: '#7A9A80', marginTop: 2 },

  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#21808D', letterSpacing: 0.5, marginBottom: 10, textTransform: 'uppercase' as const },

  imgRow: { alignItems: 'center', marginBottom: 16 },
  imgPicker: { width: 120, height: 120, borderRadius: 16, overflow: 'hidden', borderWidth: 2, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: '#0E1512', position: 'relative', alignItems: 'center', justifyContent: 'center' },
  imgPreview: { width: 120, height: 120 },
  imgPlaceholder: { alignItems: 'center', gap: 4 },
  imgPlaceholderTxt: { fontSize: 11, color: '#7A9A80', marginTop: 4 },
  removeBtn: { marginTop: 10 },
  removeBtnTxt: { fontSize: 13, color: '#ef4444', fontWeight: '600' },

  bannerThumb: { width: 120, height: 80, borderRadius: 10, marginRight: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', overflow: 'hidden', position: 'relative' },
  bannerImg: { width: 120, height: 80 },
  bannerDel: { position: 'absolute', top: 4, right: 4, backgroundColor: '#ef4444', borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 },
  bannerAdd: { width: 90, height: 80, borderRadius: 10, borderWidth: 2, borderColor: 'rgba(255,255,255,0.09)', borderStyle: 'dashed' as const, alignItems: 'center', justifyContent: 'center', backgroundColor: '#0E1512', position: 'relative' },
});
